import axios from 'axios';

/**
 * Auth refresh interceptor
 * ----------------------------------------------------------------
 * When any axios request returns 401, this interceptor:
 *   1. Silently calls POST /api/auth/refresh (which reads the
 *      httpOnly refresh_token cookie and rotates the access cookie)
 *   2. Updates the Bearer header with the new access token
 *   3. Retries the original failed request
 *
 * Single-flight: if many requests fail with 401 at the same time,
 * only one /auth/refresh call is in flight; others wait and reuse it.
 *
 * If refresh itself fails (refresh cookie expired or invalidated),
 * we call `onAuthFailure()` which clears React state and redirects
 * to /login.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// URLs that must NOT trigger a refresh attempt (otherwise infinite loop)
const SKIP_REFRESH_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
];

let refreshPromise = null;
let interceptorId = null;

function shouldSkip(url) {
  if (!url) return false;
  return SKIP_REFRESH_PATHS.some((p) => url.includes(p));
}

export function installAuthInterceptor({ onAccessToken, onAuthFailure }) {
  // Remove any previous interceptor so HMR/StrictMode double-mounts don't stack them
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }

  interceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;

      // Only attempt refresh once per request, and only for auth failures
      // on protected routes.
      if (
        status !== 401 ||
        !original ||
        original._retried ||
        shouldSkip(original.url || '')
      ) {
        return Promise.reject(error);
      }

      original._retried = true;

      try {
        // Single-flight: share one refresh call across all in-flight 401s
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => {
              // Clear the lock once the request settles (success OR failure)
              setTimeout(() => { refreshPromise = null; }, 0);
            });
        }

        const refreshResp = await refreshPromise;
        const newAccessToken = refreshResp?.data?.access_token;
        if (newAccessToken) {
          // Update default Bearer header so all subsequent requests pick it up.
          axios.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
          // Also update the failed request's Authorization header.
          if (!original.headers) original.headers = {};
          original.headers['Authorization'] = `Bearer ${newAccessToken}`;
          // Surface the new token to React so AuthContext state stays in sync.
          if (typeof onAccessToken === 'function') onAccessToken(newAccessToken);
        }
        return axios(original);
      } catch (refreshError) {
        // Refresh failed — session is truly dead. Clear & boot to login.
        if (typeof onAuthFailure === 'function') onAuthFailure();
        return Promise.reject(refreshError);
      }
    }
  );
}
